/* eslint-disable max-len */
import {SERVICE_NAMES} from '../constants';
import {createApiCall} from '../utils';
import type {ServiceName} from '../constants';
import type {
    ApiCallOptions,
    PaginatedResponse,
    User,
    UserAttributes,
    UserEmailAlias,
    CreateUserRequest,
    UpdateUserRequest,
    UpdateUserAttributesRequest,
    UpdatePasswordRequest,
    ResetPasswordRequest,
    ResetPasswordWithTokenRequest,
    SetSessionAccountRequest,
    CreateEmailAliasRequest,
    ConfirmEmailAliasRequest,
    CreateAvatarRequest,
    ActivateUserRequest,
    ForgotPasswordRequest,
    UsersListParams,
    UsersCountParams,
    GetUserByEmailParams,
    CheckUniqueEmailParams
} from './api.d';

const createUserApiCall = <T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => createApiCall<T>(SERVICE_NAMES.USER as ServiceName, method, endpoint);

export const userApi = {
    getUsersCount: ({params}: {params?: UsersCountParams}) => createUserApiCall<{count: string}>('GET', 'users/count')({params}),

    getUsers: ({params}: {params?: UsersListParams}) => createUserApiCall<PaginatedResponse<User>>('GET', 'users')({params}),

    createUser: ({data, options}: {data: CreateUserRequest; options?: ApiCallOptions}) => createUserApiCall<User>('POST', 'users')({...options, data}),

    getUser: ({userId, options}: {userId: string; options?: ApiCallOptions}) => createUserApiCall<User>('GET', `users/${userId}`)({...options}),

    updateUser: ({userId, data, options}: {userId: string; data: UpdateUserRequest; options?: ApiCallOptions}) => createUserApiCall<User>('PUT', `users/${userId}`)({...options, data}),

    deleteUser: ({userId, options}: {userId: string; options?: ApiCallOptions}) => createUserApiCall<void>('DELETE', `users/${userId}`)({...options}),

    getUserByEmail: ({params, options}: {params: GetUserByEmailParams; options?: ApiCallOptions}) => createUserApiCall<User>('GET', 'users/by-email')({...options, params}),

    getUserTokenFeatures: ({userId, options}: {userId: string; options?: ApiCallOptions}) => createUserApiCall<unknown>('GET', `users/${userId}/token-features`)({...options}),

    checkUniqueEmail: ({params}: {params: CheckUniqueEmailParams}) => createUserApiCall<Record<string, never>>('GET', 'users/unique-email')({params}),

    updatePassword: ({userId, data, options}: {userId: string; data: UpdatePasswordRequest; options?: ApiCallOptions}) => createUserApiCall<User>('PATCH', `users/${userId}/update-password`)({...options, data}),

    resetPassword: ({userId, data, options}: {userId: string; data: ResetPasswordRequest; options?: ApiCallOptions}) => createUserApiCall<void>('PATCH', `users/${userId}/reset-password`)({...options, data}),

    updateIdpAttributes: ({userId, options}: {userId: string; options?: ApiCallOptions}) => createUserApiCall<void>('PATCH', `users/${userId}/idp-attributes`)({...options}),

    setSessionAccount: ({userId, data, options}: {userId: string; data: SetSessionAccountRequest; options?: ApiCallOptions}) => createUserApiCall<void>('PATCH', `users/${userId}/set-session-account`)({...options, data}),

    getUserAttributes: ({userId, ...options}: {userId: string} & ApiCallOptions) => createUserApiCall<UserAttributes>('GET', `users/${userId}/attributes`)({...options}),

    updateUserAttributes: ({userId, data, options}: {userId: string; data: UpdateUserAttributesRequest; options?: ApiCallOptions}) => createUserApiCall<UserAttributes>('PUT', `users/${userId}/attributes`)({...options, data}),

    createAvatar: ({userId, data, options}: {userId: string; data: CreateAvatarRequest; options?: ApiCallOptions}) => createUserApiCall<User>('POST', `users/${userId}/avatar`)({...options, data}),

    deleteAvatar: ({userId, options}: {userId: string; options?: ApiCallOptions}) => createUserApiCall<void>('DELETE', `users/${userId}/avatar`)({...options}),

    getAvatar: ({userId, options}: {userId: string; options?: ApiCallOptions}) => createUserApiCall<ArrayBuffer>('GET', `users/${userId}/avatar`)({...options, responseType: 'arraybuffer'}),

    createEmailAlias: ({userId, data, options}: {userId: string; data: CreateEmailAliasRequest; options?: ApiCallOptions}) => createUserApiCall<UserEmailAlias>('POST', `users/${userId}/email-aliases`)({...options, data}),

    getEmailAliases: ({userId, options}: {userId: string; options?: ApiCallOptions}) => createUserApiCall<UserEmailAlias[]>('GET', `users/${userId}/email-aliases`)({...options}),

    deleteEmailAlias: ({userId, aliasId, options}: {userId: string; aliasId: string; options?: ApiCallOptions}) => createUserApiCall<void>('DELETE', `users/${userId}/email-aliases/${aliasId}`)({...options}),

    requestEmailAliasConfirmationCode: ({userId, aliasId, options}: {userId: string; aliasId: string; options?: ApiCallOptions}) => createUserApiCall<UserEmailAlias>('POST', `users/${userId}/email-aliases/${aliasId}/request-confirmation-code`)({...options}),

    confirmEmailAlias: ({
        userId, aliasId, data, options
    }: {userId: string; aliasId: string; data: ConfirmEmailAliasRequest; options?: ApiCallOptions}) => createUserApiCall<UserEmailAlias>('POST', `users/${userId}/email-aliases/${aliasId}/confirm`)({...options, data}),

    activateUser: ({userId, data, options}: {userId: string; data: ActivateUserRequest; options?: ApiCallOptions}) => createUserApiCall<User>('PUT', `users/${userId}/activate`)({...options, data}),

    forgotPassword: ({data, options}: {data: ForgotPasswordRequest; options?: ApiCallOptions}) => createUserApiCall<void>('POST', 'forgot-password')({...options, data, useS2SAuth: true}),

    resetPasswordWithToken: ({userId, data, params, options}: {userId: string; data: ResetPasswordWithTokenRequest; params: {token: string}; options?: ApiCallOptions}) => createUserApiCall<void>('PATCH', `users/${userId}/reset-password`)({...options, data, params, useS2SAuth: true})
};
